import { getGatewayConfig } from '@/lib/env-config';
import { apiService } from './api-service';
import { cloudFunctionService } from './cloud-function-service';
import { gatewayService } from './gateway-service';
import { retryQueueService } from './retry-queue';
import { supabase } from './supabase';

// Types for AI Scribe API
export interface AiScribeUploadRequest {
  partNumber: number;
  createdBy: string;
  audioFile: File;
  scribeJobId: string;
  isFinalPart?: boolean;
  durationSeconds?: number; // Duration of the audio chunk in seconds
  sectionTag?: string; // Optional section tag for section-based recordings
  dxList?: any; // Optional message data from parent window
  clientPlatform?: string; // Identifies the client app (e.g. 'web', 'ios-ipad')
  patientCode?: string; // UI patient ID from external system (e.g. EyePegasus)
  authToken?: string; // Bearer token for gateway auth (gateway JWT for public, Supabase JWT for in-app)
  transcription?: string; // Pre-computed transcript (from streaming Deepgram) — n8n uses this directly when present
  // n8n previously parsed locationId/appointmentId from the audioUrl path
  // (e.g. exams/{loc}/{patient}/{appt}/audio/...). With the new
  // architecture, audioUrl is empty in the gateway call, so the IDs need
  // to travel as explicit fields. The Java `ScribeRequest` DTO already
  // accepts them (`Long locationId`, `Long appointmentId`); we just need
  // to populate them on the client side.
  locationId?: string | number;
  appointmentId?: string | number;
  // Per-location feature flag forwarded all the way through to n8n.
  // Source: EpModuleService.isEnabled('AI Scribe - ICD10 Coding') on
  // the EyePegasus side → iframe URL param →
  // PublicRecordingParams.icd10CodingEnabled →
  // ScribeRequest.icd10CodingEnabled. Each upload helper passes it
  // through; gateway controllers don't introspect it.
  icd10CodingEnabled?: boolean;
}

export interface AiScribeUploadResponse {
  success: boolean;
  error?: string;
  message?: string;
  uploadId?: string;
  partNumber?: number;
  partId?: string; // Add this new field from API response
  // Returned ONLY by /api/scribe/global-workflow — the canonical
  // sectionTag list n8n queued for per-section LLM extraction. The
  // EHR uses it to flip each matching test card into the Processing /
  // skeleton state immediately after the upload ack.
  sections?: string[];
  // Raw n8n response body forwarded by the gateway (passthrough). Kept
  // alongside the parsed `sections` so any downstream consumer can
  // inspect additional fields n8n might add later without another API
  // change.
  n8nResponse?: unknown;
}

export interface AiScribeCompleteRequest {
  scribeJobId: string;
}

export interface AiScribeCompleteResponse {
  success: boolean;
  error?: string;
  message?: string;
  jobId?: string;
  estimatedCompletionTime?: string;
  audioUrl?: string;
  transcriptUrl?: string;
  urlValidityHours?: number;
  scribeJobId?: string;
  status?: string;
}

export interface AiScribeGetJobDetailsRequest {
  scribeJobId: string;
}

export interface AiScribeJobSection {
  id: string;
  scribeJobId: string;
  sectionName: string;
  originalMd: string;
  originalJson: string;
  modifiedMd: string;
  modifiedJson: string;
  originalJsonKey?: string; // Public URL to fetch section data
}

export interface AiScribeJobPart {
  id: string;
  scribeJobId: string;
  partNumber: number;
  status: 'CREATED' | 'STARTED' | 'RECORDING' | 'PAUSED' | 'COMPLETED';
  audioFileKey: string;
  textFileKey: string;
  jsonFileKey?: string;
  startTime: string;
  endTime: string | null;
  scribeJob: null;
  finalPart: boolean;
}

export interface AiScribeJob {
  id: string;
  locationId: string;
  patientId: string;
  appointmentId: string;
  ptFirstName: string;
  ptLastName: string;
  appointmentStartTime: string;
  ptDob: string;
  ptGender: string;
  service: string;
  audioFileKey: string;
  transcriptFileKey: string;
  audioFileUrl: string;
  transcriptFileUrl: string;
  status: 'CREATED' | 'STARTED' | 'RECORDING' | 'PAUSED' | 'COMPLETED';
  eyecareReportKey: string;
  startTime: string;
  createdBy: string;
  endTime: string;
  parts: AiScribeJobPart[];
  sections: AiScribeJobSection[];
}

export interface AiScribeGetJobDetailsResponse {
  job: AiScribeJob;
  message: string | null;
}

export interface AiScribeDataRequest {
  appointmentId: number;
  patientId: number;
  locationId: number;
  checkStatusOnly?: boolean;
}

export interface AiScribeDataResponse {
  success: boolean;
  error?: string;
  exception?: string;
  isJobCompleted?: boolean;
  summary?: string;
  transcript?: string;
  audioUrl?: string;
  lastPartNumber?: number;
  jobId?: string;
  processingStatus?: 'pending' | 'processing' | 'completed' | 'failed';
  estimatedCompletionTime?: string;
}

export interface AiScribeApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface AiScribeGetPublicUrlByFileKeyRequest {
  fileKey: string;
}

export interface AiScribeGetPublicUrlByFileKeyResponse {
  signedUrl: string;
}

/**
 * Tracks in-flight completeRecording calls to prevent duplicate invocations
 */
/**
 * Pull the per-section list out of n8n's global-workflow response.
 * The workflow returns the canonical sectionTag keys for every
 * section it queued for LLM extraction so the EHR can put each
 * matching test card into its Processing / skeleton state right
 * away — before the per-section /post-scribe-data calls land.
 *
 * Tolerant of THREE shapes n8n has been observed to emit (the
 * workflow has evolved over time):
 *
 *   1. `["ccHpi", "external", ...]`                — flat string
 *      array. Current observed shape.
 *   2. `[ { "sections": ["a","b"] } ]`             — array-wrapped
 *      object. Earlier sample.
 *   3. `{ "sections": ["a","b"] }`                 — bare object.
 *      Potential future flatten.
 *
 * Returns an empty array on any parse miss — caller falls back to
 * "clear spinner, no skeletons" which is the right degraded state.
 */
function extractSectionsFromGlobalResponse(body: unknown): string[] {
  if (body == null) return [];
  const asStringArray = (arr: unknown[]): string[] =>
    arr
      .filter((s: unknown): s is string => typeof s === 'string')
      .map((s: string) => s.trim())
      .filter((s: string) => s.length > 0);

  if (Array.isArray(body)) {
    if (body.length === 0) return [];
    // Shape 1: flat string array.
    if (typeof body[0] === 'string') {
      return asStringArray(body);
    }
    // Shape 2: array-wrapped object carrying `sections`.
    const wrappedSections = (body[0] as any)?.sections;
    if (Array.isArray(wrappedSections)) {
      return asStringArray(wrappedSections);
    }
    return [];
  }
  if (typeof body === 'object') {
    // Shape 3: bare object with `sections`.
    const sections = (body as any).sections;
    if (Array.isArray(sections)) {
      return asStringArray(sections);
    }
  }
  return [];
}

const pendingCompleteRecordings = new Map<
  string,
  Promise<AiScribeApiResponse<AiScribeCompleteResponse>>
>();

/**
 * AI Scribe Service for Google Cloud Functions
 */
export const aiScribeService = {
  /**
   * Upload audio chunk to Google Cloud Function
   */
  async uploadAudioChunk(
    request: AiScribeUploadRequest,
  ): Promise<AiScribeApiResponse<AiScribeUploadResponse>> {
    try {
      // Prepare form data for file upload
      const formData = new FormData();
      formData.append('data', request.audioFile);

      // Get audio upload URL from environment configuration
      const gatewayConfig = getGatewayConfig();
      if (!gatewayConfig.baseUrl) {
        console.error('❌ Gateway API base URL not configured');
        return {
          success: false,
          error: 'Audio upload URL not configured in environment',
        };
      }

      if (!request.scribeJobId) {
        console.error(
          '❌ Transcription ID not provided, cannot upload audio chunk',
        );
        return {
          success: false,
          error: 'Transcription ID not provided',
        };
      }

      let uploadUrl = `${gatewayConfig.baseUrl}/api/scribe/transcribe-audio-part`;
      // Add metadata to formData
      const isFinalPart = request.isFinalPart ?? false;
      const durationSeconds = request.durationSeconds ?? 0;
      formData.append('scribeJobId', request.scribeJobId);
      formData.append('partNumber', String(request.partNumber));
      formData.append('isFinalPart', String(isFinalPart));
      formData.append('durationSeconds', String(durationSeconds));

      // Add section tag if provided
      if (request.sectionTag) {
        formData.append('sectionTag', request.sectionTag);
      }

      // if (uploadUrl) {
      //   // Add parameters to query string
      //   const isFinalPart = request.isFinalPart ?? false;
      //   const durationSeconds = request.durationSeconds ?? 0;
      //   uploadUrl = `${uploadUrl}?scribeJobId=${request.scribeJobId}&partNumber=${request.partNumber}&isFinalPart=${isFinalPart}&durationSeconds=${durationSeconds}`;

      //   // Add section tag if provided
      //   if (request.sectionTag) {
      //     uploadUrl = `${uploadUrl}&sectionTag=${encodeURIComponent(
      //       request.sectionTag,
      //     )}`;
      //   }
      // }

      // Get Supabase session token for authenticated requests
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const authToken = session?.access_token;

      if (!authToken) {
        console.error('❌ No valid Supabase session for audio upload');
        return {
          success: false,
          error: 'User is not authenticated',
        };
      }

      // POST JSON payload to gateway with Bearer auth
      let response = await fetch(uploadUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          `❌ SECTION UPLOAD ERROR (URL mode) - Status: ${response.status}, Response: ${errorText}`,
        );

        return {
          success: false,
          error: `Section upload error: ${response.status} - ${errorText}`,
        };
      }

      const data: AiScribeUploadResponse = await response.json();

      // Log the response for debugging
      console.log(`✅ Upload successful for part ${request.partNumber}:`, {
        partId: data.partId,
        partNumber: data.partNumber,
        isFinalPart: request.isFinalPart,
        durationSeconds: request.durationSeconds,
      });

      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error(`❌ API EXCEPTION - Part ${request.partNumber}:`, error);

      return {
        success: false,
        error: `Network error: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      };
    }
  },

  /**
   * Complete recording and start AI processing
   */
  async completeRecording(
    request: AiScribeCompleteRequest,
  ): Promise<AiScribeApiResponse<AiScribeCompleteResponse>> {
    if (!request.scribeJobId) {
      return {
        success: false,
        error: 'No transcription ID provided',
      };
    }

    // Prevent duplicate calls for the same job
    const existing = pendingCompleteRecordings.get(request.scribeJobId);
    if (existing) {
      console.warn(
        `⚠️ completeRecording already in-flight for ${request.scribeJobId}, returning existing promise`,
      );
      return existing;
    }

    const promise = this._doCompleteRecording(request.scribeJobId);
    pendingCompleteRecordings.set(request.scribeJobId, promise);

    // Clean up the map when done (success or failure)
    promise.finally(() => {
      pendingCompleteRecordings.delete(request.scribeJobId);
    });

    return promise;
  },

  /** @internal Actual implementation — called at most once per scribeJobId */
  async _doCompleteRecording(
    scribeJobId: string,
  ): Promise<AiScribeApiResponse<AiScribeCompleteResponse>> {
    try {
      // Wait for all pending recordings and retry queue to complete
      await this.waitForPendingRecordings();

      console.log(`🚀 Calling complete-recording for job: ${scribeJobId}`);

      const result = await gatewayService.completeRecording(scribeJobId);

      if (result.success) {
        return {
          success: true,
          data: result.data as AiScribeCompleteResponse,
        };
      } else {
        console.error('❌ Gateway error:', result.error);
        return {
          success: false,
          error: `Failed to complete recording: ${result.error}`,
        };
      }
    } catch (error) {
      console.error('❌ Unexpected error completing recording:', error);

      return {
        success: false,
        error: `Network error: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      };
    }
  },

  /**
   * Wait for all pending recordings and retry queue to complete
   */
  async waitForPendingRecordings(): Promise<void> {
    const progressTimeout = 30000; // 30 seconds without progress
    const checkInterval = 1000; // Check every second
    let lastProgressTime = Date.now();

    console.log('Checking for pending recordings before completion...');
    retryQueueService.startProcessing();

    let lastQueueLength = retryQueueService.getStatus().queueLength;
    let lastPendingCount = cloudFunctionService.getPendingRequestsCount();

    while (true) {
      const retryStatus = retryQueueService.getStatus();
      const pendingRequests = cloudFunctionService.getPendingRequestsCount();

      if (retryStatus.queueLength === 0 && pendingRequests === 0) {
        console.log('✅ All pending recordings and uploads are complete.');
        return;
      }

      if (
        retryStatus.queueLength < lastQueueLength ||
        pendingRequests < lastPendingCount
      ) {
        lastProgressTime = Date.now();
        lastQueueLength = retryStatus.queueLength;
        lastPendingCount = pendingRequests;
      }

      if (Date.now() - lastProgressTime > progressTimeout) {
        const finalStatus = retryQueueService.getStatus();
        const finalPendingCount =
          cloudFunctionService.getPendingRequestsCount();
        console.error(
          `❌ Timeout waiting for pending recordings due to lack of progress after ${
            progressTimeout / 1000
          }s.
       There are still ${
         finalStatus.queueLength
       } items in the retry queue and ${finalPendingCount} in-flight requests.
       The system will proceed, but some audio parts might be missing.
       Remaining items in the queue will be considered failed and cleared.`,
        );
        retryQueueService.clearQueue();
        return;
      }

      await new Promise((resolve) => setTimeout(resolve, checkInterval));
    }
  },

  /**
   * Get AI Scribe job details by job ID using cloud function
   * This replaces the old polling-based getAiScribeData function
   */
  async getScribeJobDetails(
    request: AiScribeGetJobDetailsRequest,
  ): Promise<AiScribeApiResponse<AiScribeGetJobDetailsResponse>> {
    try {
      if (!request.scribeJobId) {
        return {
          success: false,
          error: 'Scribe job ID is required',
        };
      }

      const result = await gatewayService.getJobDetailsByJobId(
        request.scribeJobId,
      );

      if (result.success) {
        return {
          success: true,
          data: result.data as AiScribeGetJobDetailsResponse,
        };
      } else {
        console.error('❌ Gateway error:', result.error);
        return {
          success: false,
          error: `Failed to get job details: ${result.error}`,
        };
      }
    } catch (error) {
      console.error('❌ Unexpected error getting job details:', error);

      return {
        success: false,
        error: `Network error: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      };
    }
  },

  /**
   * @deprecated This function is deprecated and will be removed. Use getScribeJobDetails instead.
   * TODO: Remove this function after migrating all usages to getScribeJobDetails
   * Get AI Scribe data (summary, transcript, etc.)
   */
  async getAiScribeData(
    request: AiScribeDataRequest,
  ): Promise<AiScribeApiResponse<AiScribeDataResponse>> {
    console.warn(
      '⚠️ getAiScribeData is deprecated. Please use getScribeJobDetails instead.',
    );

    try {
      // Get authentication token
      const accessToken = await apiService.getAccessToken();
      if (!accessToken) {
        return {
          success: false,
          error: 'No valid authentication token found',
        };
      }

      // TODO: Replace with the correct cloud function URL for data fetch
      // For now, return a placeholder response

      return {
        success: true,
        data: {
          success: true,
          isJobCompleted: false,
          processingStatus: 'pending',
        } as AiScribeDataResponse,
      };
    } catch (error) {
      console.error('❌ Unexpected error fetching AI Scribe data:', error);

      return {
        success: false,
        error: `Network error: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      };
    }
  },

  /**
   * Get signed URL by file key via gateway (for GCS-stored files)
   */
  async getPublicUrlByFileKey(
    request: AiScribeGetPublicUrlByFileKeyRequest,
  ): Promise<AiScribeApiResponse<AiScribeGetPublicUrlByFileKeyResponse>> {
    const result = await gatewayService.getSignedUrl(request.fileKey);

    if (result.success) {
      return {
        success: true,
        data: result.data as AiScribeGetPublicUrlByFileKeyResponse,
      };
    } else {
      console.error('❌ Gateway error:', result.error);
      return {
        success: false,
        error: `Failed to get signed URL: ${result.error}`,
      };
    }
  },

  /**
   * Upload section audio with URL (instead of file)
   * Used when audio is already uploaded to storage and we only need to pass URL
   */
  async uploadSectionAudioUrl(
    audioUrl: string,
    request: Omit<AiScribeUploadRequest, 'audioFile'>,
  ): Promise<AiScribeApiResponse<AiScribeUploadResponse>> {
    try {
      // Use gateway endpoint for transcription
      const gatewayConfig = getGatewayConfig();
      if (!gatewayConfig.baseUrl) {
        console.error('❌ Gateway API base URL not configured');
        return {
          success: false,
          error: 'Gateway API base URL not configured in environment',
        };
      }

      if (!request.scribeJobId) {
        console.error(
          '❌ Scribe Job ID not provided, cannot upload section audio',
        );
        return {
          success: false,
          error: 'Scribe Job ID not provided',
        };
      }

      if (!request.sectionTag) {
        console.error(
          '❌ Section tag not provided, cannot upload section audio',
        );
        return {
          success: false,
          error: 'Section tag is required for section-based uploads',
        };
      }

      // Resolve auth token: use provided token (public recording) or get Supabase JWT (in-app)
      let authToken = request.authToken;
      if (!authToken) {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        authToken = session?.access_token;
      }

      if (!authToken) {
        console.error('❌ No authentication token available');
        return {
          success: false,
          error: 'Authentication required. No valid token found.',
        };
      }

      const uploadUrl = `${gatewayConfig.baseUrl}/api/scribe/transcribe`;

      // Prepare JSON payload with audio URL
      const isFinalPart = request.isFinalPart ?? false;
      const durationSeconds = request.durationSeconds ?? 0;
      const payload: any = {
        audioUrl,
        scribeJobId: request.scribeJobId,
        partNumber: request.partNumber,
        isFinalPart,
        durationSeconds,
        sectionTag: request.sectionTag,
        createdBy: request.createdBy,
        clientPlatform: request.clientPlatform,
        patientCode: request.patientCode,
        icd10CodingEnabled: request.icd10CodingEnabled === true,
      };
      // n8n previously derived these from the audioUrl path. With the new
      // empty-audioUrl flow they must travel as explicit fields. Numbers
      // are coerced via Number() because the URL params arrive as strings
      // and the Java DTO expects `Long`.
      if (request.locationId !== undefined && request.locationId !== '') {
        payload.locationId = Number(request.locationId);
      }
      if (
        request.appointmentId !== undefined &&
        request.appointmentId !== ''
      ) {
        payload.appointmentId = Number(request.appointmentId);
      }

      // Add dxList if provided for Assessment & Plan section
      if (request.dxList && request.sectionTag === 'assessmentAndPlan') {
        payload.dxList = request.dxList;
      }

      // Optional pre-computed transcript (from streaming Deepgram). When
      // present, the n8n workflow can skip its own transcription step.
      if (request.transcription && request.transcription.trim().length > 0) {
        payload.transcription = request.transcription;
      }

      // Log the request for debugging
      console.log(
        `✅ Section upload request (URL mode) for part ${request.partNumber}:`,
        {
          uploadUrl,
          audioUrl,
          hasDxList: !!payload.dxList,
        },
      );

      // POST JSON payload to gateway with Bearer auth
      let response = await fetch(uploadUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });

      // Handle 401 — token may have expired, try to refresh
      if (response.status === 401) {
        console.warn('⚠️ Gateway returned 401, attempting token refresh...');

        // For in-app flow: refresh Supabase session
        if (!request.authToken) {
          const {
            data: { session },
          } = await supabase.auth.getSession();
          authToken = session?.access_token;
        }
        // For public recording flow: caller must handle 401 and re-validate integration key

        if (authToken && !request.authToken) {
          response = await fetch(uploadUrl, {
            method: 'POST',
            mode: 'cors',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${authToken}`,
            },
            body: JSON.stringify(payload),
          });
        }

        if (response.status === 401) {
          console.error('❌ Authentication failed after token refresh');
          return {
            success: false,
            error:
              'Authentication failed. Please try again or contact support.',
          };
        }
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          `❌ SECTION UPLOAD ERROR (URL mode) - Status: ${response.status}, Response: ${errorText}`,
        );

        return {
          success: false,
          error: `Section upload error: ${response.status} - ${errorText}`,
        };
      }

      const data: AiScribeUploadResponse = await response.json();

      // Log the response for debugging
      console.log(
        `✅ Section upload successful (URL mode) for part ${request.partNumber}:`,
        {
          partId: data.partId,
          partNumber: data.partNumber,
          isFinalPart: request.isFinalPart,
          durationSeconds: request.durationSeconds,
          sectionTag: request.sectionTag,
        },
      );

      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error(
        `❌ SECTION UPLOAD EXCEPTION (URL mode) - Part ${request.partNumber}:`,
        error,
      );

      return {
        success: false,
        error: `Network error: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      };
    }
  },

  /**
   * Submit a combined Slit-Lamp + Ophthalmoscopy transcript to the
   * dedicated /api/scribe/transcribe-medical-exams endpoint. Used by the
   * "Access medical" voice command. Same body shape as
   * uploadSectionAudioUrl — the request DTO is identical (ScribeRequest);
   * the only difference is the endpoint path and the n8n workflow it
   * routes to. n8n's transcribe-medical-exams workflow splits the
   * transcription into per-section data downstream.
   */
  async uploadMedicalExamsTranscript(
    request: Omit<AiScribeUploadRequest, 'audioFile'>,
  ): Promise<AiScribeApiResponse<AiScribeUploadResponse>> {
    try {
      const gatewayConfig = getGatewayConfig();
      if (!gatewayConfig.baseUrl) {
        return {
          success: false,
          error: 'Gateway API base URL not configured in environment',
        };
      }
      if (!request.scribeJobId) {
        return { success: false, error: 'Scribe Job ID not provided' };
      }
      if (!request.transcription || !request.transcription.trim()) {
        return {
          success: false,
          error: 'Transcription text is required for medical-exams upload',
        };
      }

      let authToken = request.authToken;
      if (!authToken) {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        authToken = session?.access_token;
      }
      if (!authToken) {
        return {
          success: false,
          error: 'Authentication required. No valid token found.',
        };
      }

      const uploadUrl = `${gatewayConfig.baseUrl}/api/scribe/transcribe-medical-exams`;
      const payload: any = {
        scribeJobId: request.scribeJobId,
        partNumber: request.partNumber ?? 1,
        isFinalPart: request.isFinalPart ?? true,
        durationSeconds: request.durationSeconds ?? 0,
        sectionTag: request.sectionTag,
        createdBy: request.createdBy,
        clientPlatform: request.clientPlatform,
        patientCode: request.patientCode,
        transcription: request.transcription,
        icd10CodingEnabled: request.icd10CodingEnabled === true,
      };
      // Explicit IDs (formerly derived from audioUrl path by n8n).
      if (request.locationId !== undefined && request.locationId !== '') {
        payload.locationId = Number(request.locationId);
      }
      if (
        request.appointmentId !== undefined &&
        request.appointmentId !== ''
      ) {
        payload.appointmentId = Number(request.appointmentId);
      }
      console.log('✅ Medical-exams transcript upload:', {
        uploadUrl,
        sectionTag: request.sectionTag,
        scribeJobId: request.scribeJobId,
        transcriptionLength: request.transcription.length,
      });

      const response = await fetch(uploadUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          `❌ MEDICAL-EXAMS UPLOAD ERROR — Status: ${response.status}, Response: ${errorText}`,
        );
        return {
          success: false,
          error: `Medical-exams upload error: ${response.status} - ${errorText}`,
        };
      }

      const data: AiScribeUploadResponse = await response.json();
      return { success: true, data };
    } catch (error) {
      console.error('❌ MEDICAL-EXAMS UPLOAD EXCEPTION:', error);
      return {
        success: false,
        error: `Network error: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      };
    }
  },

  /**
   * Submit the full encounter transcript to the gateway's
   * /api/scribe/global-workflow endpoint. Triggered by the "Generate
   * Document" button beside the Access chip — n8n's global workflow
   * fans out per-section extraction across every test in the
   * encounter and writes back via the existing /post-scribe-data
   * webhook.
   *
   * Same ScribeRequest body + Bearer-auth pattern as
   * uploadMedicalExamsTranscript — only the endpoint path differs.
   */
  async uploadGlobalWorkflowTranscript(
    request: Omit<AiScribeUploadRequest, 'audioFile'>,
  ): Promise<AiScribeApiResponse<AiScribeUploadResponse>> {
    try {
      const gatewayConfig = getGatewayConfig();
      if (!gatewayConfig.baseUrl) {
        return {
          success: false,
          error: 'Gateway API base URL not configured in environment',
        };
      }
      if (!request.scribeJobId) {
        return { success: false, error: 'Scribe Job ID not provided' };
      }
      if (!request.transcription || !request.transcription.trim()) {
        return {
          success: false,
          error: 'Transcription text is required for global workflow upload',
        };
      }

      let authToken = request.authToken;
      if (!authToken) {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        authToken = session?.access_token;
      }
      if (!authToken) {
        return {
          success: false,
          error: 'Authentication required. No valid token found.',
        };
      }

      const uploadUrl = `${gatewayConfig.baseUrl}/api/scribe/global-workflow`;
      const payload: any = {
        scribeJobId: request.scribeJobId,
        partNumber: request.partNumber ?? 1,
        isFinalPart: request.isFinalPart ?? true,
        durationSeconds: request.durationSeconds ?? 0,
        sectionTag: 'global',
        createdBy: request.createdBy,
        clientPlatform: request.clientPlatform,
        patientCode: request.patientCode,
        transcription: request.transcription,
        icd10CodingEnabled: request.icd10CodingEnabled === true,
      };
      if (request.locationId !== undefined && request.locationId !== '') {
        payload.locationId = Number(request.locationId);
      }
      if (
        request.appointmentId !== undefined &&
        request.appointmentId !== ''
      ) {
        payload.appointmentId = Number(request.appointmentId);
      }
      if (request.dxList) {
        payload.dxList = request.dxList;
      }
      console.log('✅ Global workflow transcript upload:', {
        uploadUrl,
        scribeJobId: request.scribeJobId,
        transcriptionLength: request.transcription.length,
        hasDxList: !!payload.dxList,
      });

      const response = await fetch(uploadUrl, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          `❌ GLOBAL WORKFLOW UPLOAD ERROR — Status: ${response.status}, Response: ${errorText}`,
        );
        return {
          success: false,
          error: `Global workflow upload error: ${response.status} - ${errorText}`,
        };
      }

      // /global-workflow is a PASSTHROUGH endpoint: the gateway
      // returns n8n's body verbatim (no ack envelope). n8n's shape:
      //   [ { "sections": ["ccHpi", "external", ...] } ]
      // We tolerate a bare object { "sections": [...] } too in case
      // the workflow flattens its output later.
      const rawText = await response.text();
      let n8nResponse: unknown = null;
      try {
        n8nResponse = JSON.parse(rawText);
      } catch (parseErr) {
        console.warn(
          '[GlobalWorkflow] Could not parse n8n response as JSON — treating as empty',
          parseErr,
        );
      }
      const sections = extractSectionsFromGlobalResponse(n8nResponse);
      console.log('[GlobalWorkflow] n8n response parsed', {
        sectionCount: sections.length,
        sections,
      });
      const data: AiScribeUploadResponse = {
        success: true,
        sections,
        n8nResponse,
      };
      return { success: true, data };
    } catch (error) {
      console.error('❌ GLOBAL WORKFLOW UPLOAD EXCEPTION:', error);
      return {
        success: false,
        error: `Network error: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      };
    }
  },

  /**
   * Generate a TTS-ready clinical narrative from raw exam findings via
   * the gateway's /api/scribe/clinical-narrative endpoint. The gateway
   * forwards to the n8n /webhook/clinical-narrative workflow which
   * strips normal values, asks Gemini for a spoken summary, and returns
   * { narrative, per_section_narrative, findings, ... } unchanged.
   *
   * Used by the SPEAK voice command ("speak to me the external history")
   * — the iframe consumes per_section_narrative[target] and pipes it
   * into Google Chirp HD TTS.
   */
  async generateClinicalNarrative(
    payload: {
      external?: Record<string, unknown> | null;
      internal?: Record<string, unknown> | null;
      refraction?: Record<string, unknown> | null;
      chief_complaint?: Record<string, unknown> | string | null;
      normal_findings?: Record<string, unknown>;
      encounter_id?: string | number | null;
      audience?: 'clinician' | 'patient';
      output_mode?: 'full' | 'per_section' | 'abnormal_only';
    },
    authToken?: string,
  ): Promise<
    AiScribeApiResponse<{
      success: boolean;
      encounter_id?: string | null;
      audience?: string;
      has_abnormalities?: boolean;
      narrative?: string;
      per_section_narrative?: {
        chief_complaint?: string;
        refraction?: string;
        internal?: string;
        external?: string;
      };
      findings?: Record<string, unknown>;
      metadata?: Record<string, unknown>;
      error?: string;
    }>
  > {
    try {
      const gatewayConfig = getGatewayConfig();
      if (!gatewayConfig.baseUrl) {
        return {
          success: false,
          error: 'Gateway API base URL not configured in environment',
        };
      }

      let token = authToken;
      if (!token) {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        token = session?.access_token;
      }
      if (!token) {
        return {
          success: false,
          error: 'Authentication required. No valid token found.',
        };
      }

      const url = `${gatewayConfig.baseUrl}/api/scribe/clinical-narrative`;
      console.log('[ClinicalNarrative] POST', url, {
        hasExternal: !!payload.external,
        hasInternal: !!payload.internal,
        hasRefraction: !!payload.refraction,
        hasChiefComplaint: !!payload.chief_complaint,
        audience: payload.audience || 'clinician',
        encounter_id: payload.encounter_id,
      });

      const response = await fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          audience: 'clinician',
          output_mode: 'per_section',
          ...payload,
        }),
      });

      const responseText = await response.text();
      if (!response.ok) {
        console.error(
          `❌ CLINICAL NARRATIVE ERROR — Status: ${response.status}, Response: ${responseText}`,
        );
        return {
          success: false,
          error: `Clinical narrative error: ${response.status} - ${responseText.slice(
            0,
            200,
          )}`,
        };
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (err) {
        return {
          success: false,
          error: `Clinical narrative: invalid JSON response — ${
            err instanceof Error ? err.message : String(err)
          }`,
        };
      }
      return { success: true, data };
    } catch (error) {
      console.error('❌ CLINICAL NARRATIVE EXCEPTION:', error);
      return {
        success: false,
        error: `Network error: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      };
    }
  },

  /**
   * Upload audio chunk for section-based recording to generate section JSON
   */
  async uploadSectionAudioChunk(
    request: AiScribeUploadRequest,
  ): Promise<AiScribeApiResponse<AiScribeUploadResponse>> {
    try {
      // Prepare form data for file upload
      const formData = new FormData();
      formData.append('file', request.audioFile);

      // Get section upload URL from environment configuration
      const gatewayConfig = getGatewayConfig();
      if (!gatewayConfig.baseUrl) {
        console.error('❌ Gateway API base URL not configured');
        return {
          success: false,
          error: 'Gateway API base URL not configured in environment',
        };
      }
      let uploadUrl = `${gatewayConfig.baseUrl}/api/scribe/transcribe`;

      if (!uploadUrl) {
        console.error('❌ Section upload URL not configured');
        return {
          success: false,
          error: 'Section upload URL not configured in environment',
        };
      }

      if (!request.scribeJobId) {
        console.error(
          '❌ Scribe Job ID not provided, cannot upload section audio chunk',
        );
        return {
          success: false,
          error: 'Scribe Job ID not provided',
        };
      }

      if (!request.sectionTag) {
        console.error(
          '❌ Section tag not provided, cannot upload section audio chunk',
        );
        return {
          success: false,
          error: 'Section tag is required for section-based uploads',
        };
      }

      // Build the upload URL with query parameters
      const isFinalPart = request.isFinalPart ?? false;
      const durationSeconds = request.durationSeconds ?? 0;
      uploadUrl = `${uploadUrl}?scribeJobId=${request.scribeJobId}&partNumber=${
        request.partNumber
      }&isFinalPart=${isFinalPart}&durationSeconds=${durationSeconds}&sectionTag=${encodeURIComponent(
        request.sectionTag,
      )}`;

      // Add client platform identifier if provided
      if (request.clientPlatform) {
        formData.append('clientPlatform', request.clientPlatform);
      }

      // Add messageData to formData if provided
      if (request.dxList && request.sectionTag === 'assessmentAndPlan') {
        formData.append('dxList', JSON.stringify(request.dxList));
      }

      // Log the request for debugging
      console.log(`✅ Section upload request for part ${request.partNumber}:`, {
        uploadUrl: uploadUrl,
      });

      console.log('✅ Form data > Dx List:', formData.get('dxList'));

      // POST directly to the n8n webhook URL
      const response = await fetch(uploadUrl, {
        method: 'POST',
        mode: 'cors',
        body: formData,
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          `❌ SECTION UPLOAD ERROR - Status: ${response.status}, Response: ${errorText}`,
        );

        return {
          success: false,
          error: `Section upload error: ${response.status} - ${errorText}`,
        };
      }

      const data: AiScribeUploadResponse = await response.json();

      // Log the response for debugging
      console.log(
        `✅ Section upload successful for part ${request.partNumber}:`,
        {
          partId: data.partId,
          partNumber: data.partNumber,
          isFinalPart: request.isFinalPart,
          durationSeconds: request.durationSeconds,
          sectionTag: request.sectionTag,
        },
      );

      return {
        success: true,
        data,
      };
    } catch (error) {
      console.error(
        `❌ SECTION UPLOAD EXCEPTION - Part ${request.partNumber}:`,
        error,
      );

      return {
        success: false,
        error: `Network error: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      };
    }
  },

  /**
   * Check if AI Scribe API is accessible
   */
  async checkApiHealth(): Promise<AiScribeApiResponse<boolean>> {
    try {
      // TODO: Replace with the correct cloud function URL for health check
      // For now, return a placeholder response

      return {
        success: true,
        data: true,
      };
    } catch (error) {
      return {
        success: false,
        error: `Health check error: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
      };
    }
  },
};
